import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Button, InputAdornment, Skeleton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { Select } from 'components/common';
import type { PackEntry } from 'types/shared';
import type { AdSettingsRequestAdSet, AdSettingTextEntry } from '../api';
import useAdSettings from '../api/getAdSettings';
import adSettingsMapper from '../utils/adSettings.mapper';
import { pickDefaultAdSettingItem } from '../utils/adSettingsPages';
import {
  NO_TEXTS_AVAILABLE_MESSAGE,
  collectRequiredLocales,
  getCreativesByIdFromCache,
  getMissingLocalizationMessage,
  getMissingTextLocales,
  toTextId,
} from '../utils/adSettingsLocalization';
import { mergeAdSettingsSlot } from '../validation/adSettingsValidation';
import EditLocTextModal from './EditLocTextModal';

interface MissingLocalizationIssue {
  selectedTextId: number | null;
  message: string;
}

// RHF mutates the nested settings object in place, so the watched slot keeps its
// identity after a select change. Serialize the ids the memo depends on.
const serializeSelectedTextIds = (slot: Record<string, unknown> | undefined): string =>
  JSON.stringify(
    Object.entries((slot ?? {}) as Record<string, { text_id?: unknown } | undefined>)
      .map(([flowName, setting]) => [flowName, toTextId(setting?.text_id)])
      .sort(([a], [b]) => String(a).localeCompare(String(b))),
  );

const errorOutlineSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
};

const textSelectSx = {
  '.MuiInputBase-select': { whiteSpace: 'wrap' },
  '.MuiInputBase-root': { backgroundColor: 'white' },
};

interface AdSettingsFieldsSectionProps {
  settingsIndex: number;
  fieldName: string;
  app: string;
  platform: string;
  adSets: AdSettingsRequestAdSet[];
  isPartnershipAds: boolean;
  partnershipId?: string;
  appPageId?: string;
  sectionTitle?: string;
}

const AdSettingsFieldsSection: FC<AdSettingsFieldsSectionProps> = ({
  settingsIndex,
  fieldName,
  app,
  platform,
  adSets,
  isPartnershipAds,
  partnershipId,
  appPageId = '',
  sectionTitle,
}) => {
  const { setValue, trigger, getValues } = useFormContext();
  const queryClient = useQueryClient();
  const [editingTextId, setEditingTextId] = useState<number | null>(null);
  const [isEditLocTextModalOpen, setIsEditLocTextModalOpen] = useState(false);
  const packsWithCreatives = useWatch({ name: 'packsWithCreatives' }) as
    | PackEntry[]
    | undefined;
  const settingsSlot = useWatch({ name: `${fieldName}.${settingsIndex}` }) as
    | Record<string, unknown>
    | undefined;
  const normalizedAppPageId = (appPageId ?? '').trim();
  const isPartnershipSelected = Boolean(partnershipId?.trim());
  const canFetchPartnershipAdSettings =
    !isPartnershipAds || (isPartnershipSelected && Boolean(normalizedAppPageId));
  const partnershipKey = `${(partnershipId ?? '').trim()}:${normalizedAppPageId}`;
  const previousPartnershipKeyRef = useRef<string | null>(null);

  const {
    data: adSettings,
    isError,
    error,
    isSuccess: isSuccessAdSettings,
    isLoading: isLoadingAdSettings,
    refetch: refetchAdSettings,
  } = useAdSettings({
    params: { app, platform, ad_sets: adSets },
    isPartnershipAds,
    appPageId: normalizedAppPageId,
    config: {
      select: adSettingsMapper,
      enabled: canFetchPartnershipAdSettings,
    },
  });

  useEffect(() => {
    if (!canFetchPartnershipAdSettings) {
      return;
    }

    if (!isSuccessAdSettings || !adSettings) {
      return;
    }

    const selectedSettings = adSettings.reduce<
      Record<string, { page_id: number | ''; text_id: number | '' }>
    >(
      (acc, { name, pages, texts }) => {
        const defaultPage = pickDefaultAdSettingItem(pages);
        const defaultText = pickDefaultAdSettingItem(texts);

        return {
          ...acc,
          [name]: {
            page_id: defaultPage?.id ?? '',
            text_id: defaultText?.id ?? '',
          },
        };
      },
      {},
    );

    if (isEmpty(selectedSettings)) {
      return;
    }

    const previousPartnershipKey = previousPartnershipKeyRef.current;
    // Preserve deep-linked page/text on first load; after the user changes
    // partnership, apply that partnership's template instead of keeping the old ids.
    const shouldReplaceWithPartnershipTemplate =
      isPartnershipAds &&
      previousPartnershipKey != null &&
      previousPartnershipKey !== partnershipKey;

    previousPartnershipKeyRef.current = partnershipKey;

    const existing = shouldReplaceWithPartnershipTemplate
      ? {}
      : (getValues(`${fieldName}.${settingsIndex}`) as Record<string, unknown> | undefined) ?? {};

    setValue(
      `${fieldName}.${settingsIndex}`,
      mergeAdSettingsSlot(selectedSettings, existing),
      { shouldValidate: false },
    );
    void trigger(fieldName);
  }, [
    adSettings,
    canFetchPartnershipAdSettings,
    fieldName,
    getValues,
    isPartnershipAds,
    isSuccessAdSettings,
    partnershipKey,
    setValue,
    settingsIndex,
    trigger,
  ]);

  const selectedTextIdsSignature = serializeSelectedTextIds(settingsSlot);

  const selectedTextIdsByFlow = useMemo<Map<string, number | null>>(
    () => new Map(JSON.parse(selectedTextIdsSignature) as [string, number | null][]),
    [selectedTextIdsSignature],
  );

  const missingLocalizationByFlow = useMemo<Record<string, MissingLocalizationIssue>>(() => {
    if (!isPartnershipAds || !adSettings?.length) {
      return {};
    }

    const creativesById = getCreativesByIdFromCache(queryClient);
    const requiredLocales = collectRequiredLocales(
      (packsWithCreatives ?? []).flatMap((pack) => pack.creatives ?? []),
      creativesById,
    );

    return adSettings.reduce<Record<string, MissingLocalizationIssue>>((acc, setting) => {
      const texts = setting.texts ?? [];

      if (!texts.length) {
        acc[setting.name] = {
          selectedTextId: null,
          message: NO_TEXTS_AVAILABLE_MESSAGE,
        };
        return acc;
      }

      if (requiredLocales.size === 0) {
        return acc;
      }

      const selectedTextId = selectedTextIdsByFlow.get(setting.name) ?? null;
      const selectedText = selectedTextId
        ? texts.find((text: AdSettingTextEntry) => text.id === selectedTextId)
        : pickDefaultAdSettingItem(texts);

      if (!selectedText) {
        return acc;
      }

      const missingLocales = getMissingTextLocales(selectedText, requiredLocales);
      if (!missingLocales.length) {
        return acc;
      }

      acc[setting.name] = {
        selectedTextId: selectedText.id,
        message: getMissingLocalizationMessage(missingLocales),
      };

      return acc;
    }, {});
  }, [
    adSettings,
    isPartnershipAds,
    packsWithCreatives,
    queryClient,
    selectedTextIdsByFlow,
  ]);

  useEffect(() => {
    if (!isPartnershipAds) {
      return;
    }

    const issuesMap = Object.fromEntries(
      Object.entries(missingLocalizationByFlow).map(([flowName, issue]) => [
        flowName,
        issue.message,
      ]),
    );
    setValue(`partnershipAdSettingsLocalization.${settingsIndex}`, issuesMap, {
      shouldValidate: false,
    });
    void trigger();
  }, [isPartnershipAds, missingLocalizationByFlow, setValue, settingsIndex, trigger]);

  const handleOpenEditLocTextModal = useCallback((textId: number | null) => {
    setEditingTextId(textId);
    setIsEditLocTextModalOpen(true);
  }, []);

  const handleCloseEditLocTextModal = useCallback(() => {
    setIsEditLocTextModalOpen(false);
  }, []);

  const handleLocTextModalSavedOrClosed = useCallback(() => {
    void refetchAdSettings();
    void trigger();
  }, [refetchAdSettings, trigger]);

  if (isPartnershipAds && !canFetchPartnershipAdSettings) {
    return (
      <Alert severity="info" variant="standard" sx={{ width: 'fit-content' }}>
        Ad settings are not loaded because partnership page is not selected.
      </Alert>
    )
  }

  if (isLoadingAdSettings) {
    return (
        <Skeleton
          height={40}
          width={'50%'}
          sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
        />
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        variant="standard"
        sx={{ width: 'fit-content' }}
        action={(
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              void refetchAdSettings();
            }}
          >
            Retry
          </Button>
        )}
      >
        {(error as { cause?: { error?: string } })?.cause?.error ??
          'Error occurred. Please try again.'
        }
      </Alert>
    );
  }

  if ((!adSettings || isEmpty(adSettings)) && !isError && canFetchPartnershipAdSettings) {
    return (
      <Alert severity="error" variant="standard" sx={{ width: 'fit-content' }}>
        Ad settings not found for selected ad sets.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sectionTitle && (
        <Typography variant="subtitle2" fontWeight={500}>
          {sectionTitle}
        </Typography>
      )}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        data-testid={`adSettingsForm-${settingsIndex}`}
      >
        {adSettings?.map((setting, i) => {
          const localizationIssue = missingLocalizationByFlow[setting.name];

          return (
          <Box
            key={`${setting.name}.${i}`}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Typography variant={'body2'}>{setting.name}</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                gap: 2,
              }}
            >
              <Box sx={{ width: '100%', display: isPartnershipAds ? 'none' : 'block' }}>
                <Select
                  disabled={!setting?.pages?.length}
                  name={`${fieldName}.${settingsIndex}.${setting.name}.page_id`}
                  sx={{ '.MuiInputBase-root': { backgroundColor: 'white' } }}
                  options={
                    setting?.pages?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    })) ?? []
                  }
                  inputLabel={'Page'}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Select
                    disabled={!setting?.texts?.length}
                    sx={localizationIssue ? errorOutlineSx : textSelectSx}
                    name={`${fieldName}.${settingsIndex}.${setting.name}.text_id`}
                    inputStartAdornment={localizationIssue
                      ? (
                        <InputAdornment position="start" sx={{ mr: 0.5 }}>
                          <Tooltip title={localizationIssue.message} placement="top" arrow>
                            <ErrorOutlineIcon
                              color="error"
                              sx={{ fontSize: 18, flexShrink: 0 }}
                              data-testid={`adSettings-text-error-icon-${settingsIndex}-${setting.name}`}
                            />
                          </Tooltip>
                        </InputAdornment>
                        )
                      : undefined}
                    options={
                      setting?.texts?.map((s) => ({
                        value: s.id,
                        label: s.name,
                      })) ?? []
                    }
                    inputLabel={'Text'}
                  />
                </Box>
                {localizationIssue ? (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ textTransform: 'none', px: 0, whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'center' }}
                    onClick={() => {
                      handleOpenEditLocTextModal(localizationIssue.selectedTextId);
                    }}
                  >
                    Add loc text
                  </Button>
                ) : null}
              </Box>
            </Box>
          </Box>
          );
        })}
      </Box>
      <EditLocTextModal
        autoAddLocalizedVersion
        textId={editingTextId}
        isOpen={isEditLocTextModalOpen}
        onClose={handleCloseEditLocTextModal}
        onSaved={handleLocTextModalSavedOrClosed}
      />
    </Box>
  );
};

export default AdSettingsFieldsSection;
